'use client';
import clsx from 'clsx';
import { KafkaNote } from '@nf/utils-common/constants';
import { CollapseAllLeague } from './components/collapse-all-league';
import { League } from './components/league';
import { MarsOddsPageProps, MatchGroup } from '../odds-page/types';
import { useAppearanceStore } from '@/store/appearance';
import { ReactNode, useEffect, useState } from 'react';
import { useAtomValue } from 'jotai';
import { earlyFilterDateAtom, oddsPageAtom } from '@/atoms/odds-page';
import { useGetSiteConfigs } from '@/hooks/query/use-site-configs';
import { useFilterSabaString } from '@/hooks/query/use-saba-filter';
import { IO } from '@nf/utils-common/fpw/monadic';
import { getGroupLeagues } from '@/utils/odds-utils';
import { useCurrentProperties } from '@/hooks/query';
import { setKafkaNormal } from '@/hooks/query/use-kafka-log';
import { css } from 'styled-system/css';
import { MarketLabel } from '@/types';
import { SwitchBetType } from './components/switch-bettype';
import { leagueService } from '@/services/league.service';
import { useObservableState } from '@nf/utils-common/hooks';
import { OddsTableMarsContextProvider } from './context';
import { GameTypeSwitch } from './components/game-type-switch';
import { GameType } from '@nf/types/odds';
import { ViewEnum } from '@/types';
import { Tooltip } from '@/components/widgets/tooltip';
import { tooltip } from 'styled-system/recipes';
import { useTranslations } from 'next-intl';
import { oddsTableStyle } from './odds-table.style';
import { isOddsPageLoadingAtom } from '@/atoms/odds-page';

import { AccountActStatus } from '@nf/utils-common/constants';
import { accountService } from '@/services/account.service';
import { useAccountInfo } from '@/hooks/query/account';
import { PopUpFeatureId } from '@nf/types/account';
import { is1x2CustomizationSettingSport } from '@/services/settings.service';

export const OddsTableMars = ({ sport, market, matchCollection, hasCollapseAllLeague = true, isMyFavourite }: MarsOddsPageProps) => {
	const { gameType } = useCurrentProperties();
	const [oddsPageLayout, fontSize, view] = useAppearanceStore((state) => [state.oddsPageLayout, state.fontSize, state.view]);
	const [matchGroups, setMatchGroups] = useState<MatchGroup[]>([]);
	const filterDate = useAtomValue(earlyFilterDateAtom);
	const oddsData = useAtomValue(oddsPageAtom);
	const isAllLeagueExpand = useObservableState(leagueService.isAllLeagueExpand$, true);
	const setIsAllLeagueExpand = (isExpand: boolean) => leagueService.setIsAllLeagueExpand(isExpand);
	const [expandedMatchCount, setExpandedMatchCount] = useState<number>(10);
	const { SiteConfigsData } = useGetSiteConfigs();
	const { filterSabaString } = useFilterSabaString();
	const t = useTranslations('General');
	const isNewbieTheme = oddsPageLayout === 'list'
	const isLoading = useAtomValue(isOddsPageLoadingAtom);

	const { user } = useAccountInfo();
	const userSetting = user?.UserSetting;

	const hasShownTooltip = accountService.getHasShownFeatureId(
		userSetting,
		PopUpFeatureId.BettypeModeNotice
	);

	const shouldShowTooltip = user?.ActStatus === AccountActStatus.Deposited && view === ViewEnum.mars && SiteConfigsData?.Enable1x2Customization && SiteConfigsData?.Enable1x2CustomizationHint && is1x2CustomizationSettingSport(sport) && !hasShownTooltip && !isLoading;

	let accumulatedGroupMatches = 0;


	const convertExpandedMatchCount = (sportId: number, expandedMatchCount?: string): number => {
		const stringsMap = new Map();
		let result = 10;
		if (expandedMatchCount !== '') {
			const values = expandedMatchCount?.split(';');
			values?.forEach(function (value) {
				const parts = value.split('_');
				if (parts.length === 2) {
					stringsMap.set(parts[0], parts[1]);
				}
			});
		}
		if (stringsMap.has(sportId.toString())) {
			result = Number(stringsMap.get(sportId.toString()));
		} else {
			result = Number(stringsMap.get('def'));
		}
		return result;
	}

	const filterSabaStringToLeague = (matchGroup: MatchGroup[]) => {
		matchGroup.forEach((match) => {
			match.LeagueName = filterSabaString(match.LeagueName, match.LeagueGroupId);
		});
		return matchGroup;
	}

	useEffect(() => {
		setExpandedMatchCount(convertExpandedMatchCount(sport, SiteConfigsData?.ExpandedMatchCount))
	}, [SiteConfigsData?.ExpandedMatchCount]);

	useEffect(() => {
		IO.of(matchCollection)
			.map(getGroupLeagues(oddsData?.leagueNameMap))
			.map(filterSabaStringToLeague)
			.map(setMatchGroups)
			.run()
	}, [matchCollection]);

	useEffect(() => {
		setIsAllLeagueExpand(true);
	}, [market, filterDate]);

	const toggleAllLeague = () => {
		setIsAllLeagueExpand(!isAllLeagueExpand);
		setKafkaNormal({
			note: KafkaNote.MarsV1,
			type: 6,
			action: isAllLeagueExpand ? 36 : 37
		});
	};

	const calExpandMatchCount = (prevAccumulatedMatchCount: number, accumulatedMatchCount: number, currentMatchCount: number) => {
		if (prevAccumulatedMatchCount > expandedMatchCount) return 0;
		return accumulatedMatchCount <= expandedMatchCount ? currentMatchCount : (expandedMatchCount - prevAccumulatedMatchCount);
	}

	return (
		<OddsTableMarsContextProvider sport={sport} gameType={gameType as GameType} market={market as MarketLabel} isMyFav={isMyFavourite}>
			<GameTypeSwitch />
			<div className={clsx('odds-table-mars', `fontsize-${fontSize}`, css({ p: '6px', position: 'relative' }))}>
				{/* TODO: newbie select bettype */}
				{isNewbieTheme ? (
					<SwitchBetType />
				) : null}
				{hasCollapseAllLeague && matchGroups.length ? (
					<CollapseAllLeague
						isAllLeagueExpand={isAllLeagueExpand}
						clickAllLeague={toggleAllLeague}
					/>) : null
				}
				{
					matchGroups?.reduce((nodes: ReactNode[], matchGroup: MatchGroup): ReactNode[] => {
						const prevAccumulatedGroupMatches = accumulatedGroupMatches;
						accumulatedGroupMatches += matchGroup.Matches.length;
						const expandMatchCount = calExpandMatchCount(prevAccumulatedGroupMatches, accumulatedGroupMatches, matchGroup.Matches.length);
						nodes.push(
							<League
								key={`${gameType}_${matchGroup.LeagueId}_${nodes.length}`}
								leagueId={matchGroup.LeagueId}
								leagueName={matchGroup.LeagueName}
								matches={matchGroup.Matches}
								isAllLeagueExpand={isAllLeagueExpand || (isAllLeagueExpand && accumulatedGroupMatches <= expandedMatchCount)}
								expandMatchCount={expandMatchCount}
								leagueGroupId={matchGroup.LeagueGroupId}
								parentMatchId={matchGroup.Matches[0].Parentmatchid}
							/>
						);
						return nodes;
					}, [])
				}
				{shouldShowTooltip ? (
					<Tooltip
						message={t('lbl_PreferredBetTypeTooltips')}
						countdownTime={3000}
						className={clsx(
							tooltip({ 
								visual: 'basic', 
								vertical: isNewbieTheme ? 'bottom' : 'top', 
								horizontal: 'left', 
								size: 'sm' 
							}), 
							oddsTableStyle.tooltip)}
						dataAttribute={{
							name: 'newbie',
							value: isNewbieTheme
						}}
					/>
				) : null}
			</div>
		</OddsTableMarsContextProvider>
	)
}